import type { ReactNode } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * The small pieces every loan panel is built from.
 *
 * One rule runs through them, the same one the customer panels follow: a value the server did not
 * send is not rendered at all, while a value it sent as null is rendered as a dash. "Withheld
 * from your role" and "never captured" are different facts, and a screen that showed them
 * identically teaches an operator to read one as the other.
 */

export function Field({
  label,
  value,
  mono = false,
  hint,
}: {
  label: string
  value: ReactNode
  mono?: boolean
  /** A line under the value, for the thing the value does not say on its own. */
  hint?: ReactNode
}) {
  const empty = value === null || value === undefined || value === ''

  return (
    <div className="min-w-0">
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className={cn('mt-0.5 text-sm break-words text-heading', mono && 'font-mono')}>
        {empty ? <span className="text-muted">—</span> : value}
      </dd>
      {hint ? <p className="mt-0.5 text-2xs text-muted">{hint}</p> : null}
    </div>
  )
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
}: {
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
        {actions}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

export function EmptyPanel({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
      <span className="text-muted" aria-hidden>
        {icon}
      </span>
      <p className="text-sm font-medium text-heading">{title}</p>
      <p className="max-w-md text-xs text-muted">{description}</p>
      {action}
    </div>
  )
}

/** A labelled amount in a row of amounts — the shape a balance summary is read in. */
export function Figure({
  label,
  children,
  emphasis = false,
}: {
  label: string
  children: ReactNode
  emphasis?: boolean
}) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <div className={cn('mt-0.5', emphasis && 'text-base font-semibold')}>{children}</div>
    </div>
  )
}
